// useCurrencies — the currencies as a picker's source (ADR-080 §3). The
// CATALOGUE is the runtime's (common/currency — Intl's ISO 4217 list, named by
// `Intl.DisplayNames` in the viewer's language): nothing to load, the same list
// on both sides of the wire. The START LIST leads with the project's own
// currency (its default, never a rule), then the currencies its countries
// usually price in, then everything A–Z by name — a row is its NAME with the
// CODE under it, found by either (a code typed whole answers alone); once
// chosen, and beside every amount, the CODE. A stored code the runtime does
// not list (another browser's ICU) is still a row, named by its code — an edit
// form always shows what it holds. Two descriptors: the CODE kind for a screen
// that already holds its countries (the platform wizard, the regional drawer),
// the SMALL kind for the asset forms — the project's countries asked whole when
// the field opens, or at once when it already holds a value (the family names
// a chosen value from its list — ADR-080 §7; its own cache entry).

import { CURRENCY_CODES } from '@ems-app/schemas/common/currency';
import type { ProjectCountry } from '@ems-app/schemas/projects/countries';
import { currencyName } from '../lib/format.js';
import { fetchProjectCountries, projectCountriesKeys } from './useProjectCountries.js';
import { codeSource, type CodeSource, type OptionRow, type SmallSource } from './useOptions.js';

export interface CurrencyLead {
  /** The project's own currency — the first row (its group says why). */
  project?: string | null;
  /** The currencies the project's countries usually price in — the rows after it, each once. */
  countries?: readonly (string | null | undefined)[];
}

/** The rows: the lead (its groups named), then every currency the runtime knows A–Z by name, then a held code the
 *  runtime does not know. */
export function currencyRows(lang: string, lead: CurrencyLead, also?: string | null): OptionRow[] {
  const row = (code: string, group: string | null): OptionRow => ({ id: code, label: currencyName(code, lang), description: code, selectedLabel: code, pickable: true, group });
  const seen = new Set<string>();
  const rows: OptionRow[] = [];
  if (lead.project) {
    rows.push(row(lead.project, 'project_currency'));
    seen.add(lead.project);
  }
  for (const code of lead.countries ?? []) {
    if (!code || seen.has(code)) continue;
    rows.push(row(code, 'country_currencies'));
    seen.add(code);
  }
  const rest = [...CURRENCY_CODES].filter((c) => !seen.has(c)).map((c) => row(c, null));
  rest.sort((a, b) => a.label.localeCompare(b.label, lang));
  rows.push(...rest);
  if (also && !seen.has(also) && !CURRENCY_CODES.has(also)) rows.push(row(also, null));
  return rows;
}

/** The CODE kind — for a screen that already holds the countries it leads with; built once per language and lead. */
export function currencyCodeSource(lang: string, lead: CurrencyLead, also?: string | null): CodeSource {
  return codeSource(`currency:${lang}:${lead.project ?? ''}:${(lead.countries ?? []).filter(Boolean).join(',')}:${also ?? ''}`, currencyRows(lang, lead, also));
}

/** The SMALL kind — the project's countries asked whole when the field opens or holds a value (a key of its own: the rows follow the language
 *  and the project's currency too, so one cache entry per answer — `rows` stays a pure projection of its data). */
export function projectCurrencySource(projectId: string, lang: string, projectCurrency: string | null, also?: string | null): SmallSource<ProjectCountry[]> {
  return {
    kind: 'small',
    queryKey: [...projectCountriesKeys.all(projectId), 'currencies', lang, projectCurrency ?? '', also ?? ''],
    fetch: () => fetchProjectCountries(projectId),
    rows: (countries) => currencyRows(lang, { project: projectCurrency, countries: countries.map((c) => c.default_currency_code) }, also),
  };
}
